import useIsMobile from '../../lib/useIsMobile';
import MarketingNav from './MarketingNav';
import Footer from './Footer';

const C = {
  bg: '#FAFBFC',
  white: '#FFFFFF',
  navy: '#1B2B4B',
  accent: '#2563EB',
  muted: '#64748B',
  border: '#E2E8F0',
};

function Section({ title, children, isMobile }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ color: C.navy, fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: '0 0 14px' }}>{title}</h2>
      <div style={{ color: C.muted, fontSize: isMobile ? 14 : 15, lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

export default function TermsPage() {
  const isMobile = useIsMobile();

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>
      <MarketingNav />

      <section style={{ textAlign: 'center', padding: isMobile ? '60px 20px 40px' : '100px 24px 60px', maxWidth: 800, margin: '0 auto' }}>
        <h1 style={{ color: C.navy, fontSize: isMobile ? 28 : 44, fontWeight: 700, margin: '0 0 12px', lineHeight: 1.2 }}>Terms of Service</h1>
        <p style={{ color: C.muted, fontSize: isMobile ? 14 : 16, margin: 0 }}>Last updated: March 2026</p>
      </section>

      <section style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '0 20px 60px' : '0 24px 80px' }}>

        <Section title="1. Acceptance of Terms" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            By accessing or using the ClearSeg website and services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use our Services. These Terms constitute a legally binding agreement between you and ClearSeg ("we," "our," or "us").
          </p>
        </Section>

        <Section title="2. Description of Services" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            ClearSeg provides cost segregation studies for single-family rental properties. Our studies reclassify building components into shorter depreciation categories in accordance with IRS guidelines. Each study is reviewed and certified by a licensed Professional Engineer (PE) and delivered to your designated CPA or tax professional. Our Services include the online intake process, study preparation, PE certification, and digital delivery of the completed report.
          </p>
        </Section>

        <Section title="3. User Accounts" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            Certain features of our Services may require you to create an account. You agree to:
          </p>
          <ul style={{ margin: 0, paddingLeft: 24 }}>
            <li>Provide accurate, current, and complete information during registration.</li>
            <li>Maintain the security of your account credentials and not share them with others.</li>
            <li>Notify us immediately of any unauthorized access to or use of your account.</li>
            <li>Accept responsibility for all activities that occur under your account.</li>
          </ul>
        </Section>

        <Section title="4. Payment Terms" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            The fee for a single-family cost segregation study is displayed on our website at the time of purchase. All payments are processed securely through Stripe. By submitting payment, you authorize ClearSeg to charge the applicable fee to your selected payment method.
          </p>
          <p style={{ margin: 0 }}>
            All fees are quoted in U.S. dollars. Applicable taxes may be added to the total at checkout where required by law.
          </p>
        </Section>

        <Section title="5. Refund Policy" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            Fees are non-refundable once the study process has been initiated (i.e., once your property information has been submitted and work has begun). If ClearSeg is unable to complete your study for any reason, you will receive a full refund. Refund requests must be submitted to <a href="mailto:info@clearseg.com" style={{ color: C.accent, textDecoration: 'none' }}>info@clearseg.com</a>. Refunds, when applicable, will be processed within 10 business days to the original payment method.
          </p>
        </Section>

        <Section title="6. Intellectual Property" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            All content on the ClearSeg website -- including text, graphics, logos, design, software, and methodology -- is the property of ClearSeg and is protected by applicable intellectual property laws.
          </p>
          <p style={{ margin: 0 }}>
            Upon delivery and full payment, you receive a non-exclusive, non-transferable license to use your completed cost segregation study for your personal tax planning purposes. You may share the study with your CPA, tax attorney, or financial advisor. You may not resell, redistribute, or publish the study or its methodology.
          </p>
        </Section>

        <Section title="7. Use of Study / Tax Disclaimer" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            <strong style={{ color: C.navy }}>ClearSeg is not a tax advisory firm, accounting firm, or law firm.</strong> Our cost segregation studies are engineering-based analyses prepared in accordance with IRS guidelines. The studies are intended to be used by your CPA or tax professional in preparing your tax returns.
          </p>
          <p style={{ margin: '0 0 12px' }}>
            We do not provide tax advice, legal advice, or accounting services. The tax benefits described on our website and in our studies are estimates and depend on your individual tax situation. Actual tax savings may vary.
          </p>
          <p style={{ margin: 0 }}>
            You are solely responsible for how the study is applied to your tax return. We strongly recommend consulting with a qualified tax professional before making any tax-related decisions based on our study.
          </p>
        </Section>

        <Section title="8. Limitation of Liability" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            To the fullest extent permitted by law, ClearSeg and its officers, directors, employees, engineers, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our Services, including but not limited to:
          </p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 24 }}>
            <li>Tax penalties, interest, or additional taxes assessed by the IRS or any tax authority.</li>
            <li>Loss of anticipated tax savings or deductions.</li>
            <li>Errors or omissions in information you provided to us.</li>
            <li>Any decisions made by your CPA or tax professional based on our study.</li>
          </ul>
          <p style={{ margin: 0 }}>
            Our total liability for any claim arising from our Services shall not exceed the amount you paid to ClearSeg for the specific study giving rise to the claim.
          </p>
        </Section>

        <Section title="9. Indemnification" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            You agree to indemnify, defend, and hold harmless ClearSeg and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Services; (b) your violation of these Terms; (c) your violation of any third-party rights; or (d) any inaccurate or incomplete information you provided to us.
          </p>
        </Section>

        <Section title="10. Dispute Resolution" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            Any dispute, controversy, or claim arising out of or relating to these Terms or our Services shall be resolved through binding arbitration administered by the American Arbitration Association (AAA) in accordance with its Commercial Arbitration Rules.
          </p>
          <p style={{ margin: '0 0 12px' }}>
            Arbitration shall take place in the State of New Jersey. The arbitrator's decision shall be final and binding and may be entered as a judgment in any court of competent jurisdiction.
          </p>
          <p style={{ margin: 0 }}>
            <strong style={{ color: C.navy }}>Class Action Waiver:</strong> You agree that any dispute resolution proceedings will be conducted on an individual basis and not as part of any class, consolidated, or representative action.
          </p>
        </Section>

        <Section title="11. Governing Law" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            These Terms shall be governed by and construed in accordance with the laws of the State of New Jersey, without regard to its conflict of law provisions. Any legal action not subject to arbitration shall be brought exclusively in the state or federal courts located in New Jersey.
          </p>
        </Section>

        <Section title="12. Termination" isMobile={isMobile}>
          <p style={{ margin: '0 0 12px' }}>
            We reserve the right to suspend or terminate your access to our Services at any time, with or without cause, and with or without notice. Reasons for termination may include, but are not limited to:
          </p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 24 }}>
            <li>Violation of these Terms.</li>
            <li>Providing false or misleading information.</li>
            <li>Fraudulent or illegal activity.</li>
            <li>Non-payment of fees.</li>
          </ul>
          <p style={{ margin: 0 }}>
            Upon termination, your right to use the Services ceases immediately. Provisions of these Terms that by their nature should survive termination (including limitations of liability, indemnification, and dispute resolution) shall survive.
          </p>
        </Section>

        <Section title="13. Changes to Terms" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            We may modify these Terms at any time. When we make material changes, we will update the "Last updated" date at the top of this page. Your continued use of the Services after any changes constitutes acceptance of the revised Terms. If you do not agree to the updated Terms, you must stop using our Services.
          </p>
        </Section>

        <Section title="14. Contact Us" isMobile={isMobile}>
          <p style={{ margin: 0 }}>
            If you have questions about these Terms of Service, please contact us:
          </p>
          <div style={{ marginTop: 16, padding: '20px 24px', background: C.white, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <p style={{ color: C.navy, fontWeight: 600, margin: '0 0 8px' }}>ClearSeg</p>
            <p style={{ color: C.muted, margin: '0 0 4px', fontSize: 14 }}>
              Email: <a href="mailto:info@clearseg.com" style={{ color: C.accent, textDecoration: 'none' }}>info@clearseg.com</a>
            </p>
          </div>
        </Section>

      </section>

      <Footer />
    </div>
  );
}
